import React from 'react';
import {Box, Chip, Typography} from '@mui/material';
import {CustomerMemberShipNames} from '../../../../../../../core/constants/enums';
import {CustomersMembersListDataGridCellKeys} from '../../../../../../../core/constants/table/data-grid-column-keys';
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes,} from '../../../../../../../packages/data-grid';
import {formatPhoneNumber} from '../../../../../../../core/services/utils/utils';
import MemberRowActionsMenu from './member-row-actions';
import MembershipStatusChip from './membership-status-chip';
import ResendActivationIconButton from './resend-activation-button';

export const ALL_TAB = 'all';

export const dataGridSaveConfigs = {
    [ALL_TAB]: {name: 'CustomersAllMembersListViewDataGrid', version: 4},
    [CustomerMemberShipNames.active]: {name: 'CustomersActiveMembersListViewDataGrid', version: 5},
    [CustomerMemberShipNames.onHold]: {name: 'CustomersOnHoldMembersListViewDataGrid', version: 4},
    [CustomerMemberShipNames.deactivated]: {name: 'CustomersDeactivatedMembersListViewDataGrid', version: 4},
    [CustomerMemberShipNames.onHoldCompanyLost]: {name: 'CustomersOnHoldCompanyLostMembersListViewDataGrid', version: 4},
    generic: {name: 'CustomersGenericMembersListViewDataGrid', version: 4},
};

/**
 * Resolves the data-grid save config for a membership tab.
 * @param {string} tab
 * @returns {{name: string, version: number}}
 */
export const resolveDataGridSaveConfig = (tab) =>
    dataGridSaveConfigs[tab] ?? dataGridSaveConfigs.generic;

/**
 * Whether the member has a real assigned customer link.
 * @param {any} member
 * @returns {boolean}
 */
export const hasAssignedCustomer = (member) =>
    !!(member?.customer?.id || member?.customer?.code);

/**
 * Resolves Assigned Customer semantics for display / export.
 * @param {any} member
 * @param {(key: string, options?: object) => string} t
 * @returns {{kind: 'assigned' | 'pending' | 'none', label: string}}
 */
export const resolveAssignedCustomerValue = (member, t) => {
    if (hasAssignedCustomer(member)) {
        return {
            kind: 'assigned',
            label: member.customer?.name ?? '',
        };
    }
    const pendingCompany = (member?.company ?? '').trim();
    if (pendingCompany) {
        return {
            kind: 'pending',
            label: pendingCompany,
        };
    }
    return {
        kind: 'none',
        label: t('customerCell.none'),
    };
};

/**
 * Plain-text Assigned Customer value for CSV export.
 * @param {any} member
 * @param {(key: string, options?: object) => string} t
 * @returns {string}
 */
export const resolveAssignedCustomerExportValue = (member, t) => {
    const value = resolveAssignedCustomerValue(member, t);
    if (value.kind === 'pending') {
        return t('export.customerPending', {name: value.label});
    }
    if (value.kind === 'none') {
        return '';
    }
    return value.label;
};

/**
 * Resolves our i18n display title for a membership status / tab value.
 * Falls back to an optional API title, then the raw status name.
 * @param {string | null | undefined} statusOrTab
 * @param {(key: string, options?: object) => string} t
 * @param {string} [fallbackTitle]
 * @returns {string}
 */
export const resolveMembershipTitle = (statusOrTab, t, fallbackTitle) => {
    if (!statusOrTab) {
        return fallbackTitle ?? '';
    }
    if (statusOrTab === ALL_TAB) {
        return t('statusTitles.all');
    }
    const knownTitles = {
        [CustomerMemberShipNames.active]: 'statusTitles.Active',
        [CustomerMemberShipNames.onHold]: 'statusTitles.OnHold',
        [CustomerMemberShipNames.deactivated]: 'statusTitles.Deactivated',
        [CustomerMemberShipNames.onHoldCompanyLost]: 'statusTitles.OnHoldCompanyLost',
    };
    if (knownTitles[statusOrTab]) {
        return t(knownTitles[statusOrTab]);
    }
    return fallbackTitle || statusOrTab;
};

/**
 * Human-readable status label for a member row.
 * @param {any} member
 * @param {(key: string, options?: object) => string} t
 * @param {string} [fallbackTitle]
 * @returns {string}
 */
export const resolveStatusLabel = (member, t, fallbackTitle) =>
    resolveMembershipTitle(member?.memberShipName, t, fallbackTitle);

/**
 * Renders a colored membership status chip for a grid cell.
 * On Hold rows can include a resend-activation icon when the partner has a B2C website.
 * @param {any} member
 * @param {(key: string, options?: object) => string} t
 * @param {string} [fallbackTitle]
 * @param {(member: any) => void} [onResendActivation]
 * @returns {JSX.Element}
 */
export const renderStatusChip = (member, t, fallbackTitle, onResendActivation) => {
    const chip = (
        <MembershipStatusChip
            status={member?.memberShipName}
            label={resolveStatusLabel(member, t, fallbackTitle)}
        />
    );
    const showResend = typeof onResendActivation === 'function'
        && member?.memberShipName === CustomerMemberShipNames.onHold;
    if (!showResend) {
        return chip;
    }
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            {chip}
            <ResendActivationIconButton
                label={t('rowActions.resendActivation')}
                onClick={() => onResendActivation(member)}
            />
        </Box>
    );
};

/**
 * Status-aware Assigned Customer cell (real assignment vs pending company vs none).
 * @param {any} member
 * @param {(key: string, options?: object) => string} t
 * @returns {JSX.Element}
 */
export const renderAssignedCustomerCell = (member, t) => {
    const value = resolveAssignedCustomerValue(member, t);

    if (value.kind === 'none') {
        return (
            <Typography variant="body2" color="text.secondary">
                {value.label}
            </Typography>
        );
    }

    if (value.kind === 'pending') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minWidth: 0,
                    maxWidth: '100%',
                }}
            >
                <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={value.label}
                    sx={{fontStyle: 'italic', minWidth: 0}}
                >
                    {value.label}
                </Typography>
                <Chip
                    size="small"
                    label={t('customerCell.pending')}
                    color="warning"
                    variant="outlined"
                    sx={{flexShrink: 0, height: 22, fontWeight: 600}}
                />
            </Box>
        );
    }

    return (
        <Typography variant="body2" noWrap title={value.label}>
            {value.label}
        </Typography>
    );
};

/**
 * Builds the pinned actions column definition.
 * @param {(key: string) => string} t
 * @param {number} [width]
 * @returns {DataGridColumn}
 */
export const buildActionsColumn = (t, width = 70) => ({
    title: t('columns.actions'),
    name: CustomersMembersListDataGridCellKeys.actionButton,
    alignment: DataGridColumnAlignments.center,
    type: DataGridColumnTypes.element,
    sortable: false,
    resizable: false,
    pinned: true,
    pinnedType: DataGridColumnPinnedTypes.right,
    pinnedToggleable: false,
    visibilityToggleable: false,
    width,
});

/**
 * Shared member identity / contact cells used by most grids.
 * Assigned Customer is set by callers via `renderAssignedCustomerCell`.
 * @param {any} member
 * @param {any[]} countries
 * @returns {Record<string, any>}
 */
export const buildSharedMemberCells = (member, countries) => ({
    [CustomersMembersListDataGridCellKeys.name]: `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim(),
    [CustomersMembersListDataGridCellKeys.email]: member.email,
    [CustomersMembersListDataGridCellKeys.phone]: formatPhoneNumber(member.phone),
    [CustomersMembersListDataGridCellKeys.registerDate]: member.submitDateTime,
    [CustomersMembersListDataGridCellKeys.latestLogin]: member.lastLoginDate,
    [CustomersMembersListDataGridCellKeys.companyPhone]: formatPhoneNumber(member.companyPhone ?? member.customer?.phone),
    [CustomersMembersListDataGridCellKeys.country]: countries?.find((e) => e.id === member.province?.countryId)?.name,
    [CustomersMembersListDataGridCellKeys.province]: member.province?.name,
    [CustomersMembersListDataGridCellKeys.city]: member?.city,
    [CustomersMembersListDataGridCellKeys.address]: member?.address,
    [CustomersMembersListDataGridCellKeys.postalCode]: member?.postalCode,
});

/**
 * Renders the row actions menu cell.
 * @param {object} props
 * @returns {JSX.Element}
 */
export const renderMemberActionsCell = ({
                                            member,
                                            onAssign,
                                            onDetach,
                                            onDeactivate,
                                            onActivate,
                                            onDelete,
                                            onResendActivation,
                                            canAssign,
                                            canOpenProfile,
                                            hasB2cWebsite,
                                        }) => (
    <MemberRowActionsMenu
        member={member}
        onAssign={onAssign}
        onDetach={onDetach}
        onDeactivate={onDeactivate}
        onActivate={onActivate}
        onDelete={onDelete}
        onResendActivation={onResendActivation}
        canAssign={canAssign}
        canOpenProfile={canOpenProfile}
        hasB2cWebsite={hasB2cWebsite}
    />
);

/**
 * Column type aliases for status / identity grids (All, Active, Inactive, Generic).
 * @param {(key: string) => string} t
 * @param {{includeStatus?: boolean, pinName?: boolean, showActions?: boolean}} options
 * @returns {DataGridColumn[]}
 */
export const buildStandardMembersColumns = (t, {
    includeStatus = true,
    pinName = false,
    showActions = true,
} = {}) => ([
    ...(includeStatus ? [{
        title: t('columns.status'),
        name: CustomersMembersListDataGridCellKeys.status,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.element,
        sortable: false,
        width: 200,
    }] : []),
    {
        title: t('columns.name'),
        name: CustomersMembersListDataGridCellKeys.name,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
        ...(pinName ? {
            pinned: true,
            pinnedType: DataGridColumnPinnedTypes.left,
        } : {}),
        width: 200,
    },
    {
        title: t('columns.email'),
        name: CustomersMembersListDataGridCellKeys.email,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
        width: 200,
    },
    {
        title: t('columns.phone'),
        name: CustomersMembersListDataGridCellKeys.phone,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
    },
    {
        title: t('columns.customer'),
        name: CustomersMembersListDataGridCellKeys.company,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.element,
        sortable: false,
        width: 220,
    },
    {
        title: t('columns.registerDate'),
        name: CustomersMembersListDataGridCellKeys.registerDate,
        alignment: DataGridColumnAlignments.right,
        type: DataGridColumnTypes.dateTime,
    },
    {
        title: t('columns.latestLogin'),
        name: CustomersMembersListDataGridCellKeys.latestLogin,
        alignment: DataGridColumnAlignments.right,
        type: DataGridColumnTypes.dateTime,
        width: 150,
    },
    ...(showActions ? [buildActionsColumn(t)] : []),
]);

/**
 * Address-rich columns for OnHold members.
 * @param {(key: string) => string} t
 * @param {boolean} showActions
 * @param {{actionsWidth?: number}} [options]
 * @returns {DataGridColumn[]}
 */
export const buildOnHoldMembersColumns = (t, showActions = true, {actionsWidth = 70} = {}) => ([
    {
        title: t('columns.name'),
        name: CustomersMembersListDataGridCellKeys.name,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
        width: 200,
    },
    {
        title: t('columns.email'),
        name: CustomersMembersListDataGridCellKeys.email,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
        width: 200,
    },
    {
        title: t('columns.phone'),
        name: CustomersMembersListDataGridCellKeys.phone,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
    },
    {
        title: t('columns.companyInfo'),
        name: CustomersMembersListDataGridCellKeys.company,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.element,
        sortable: false,
        width: 220,
    },
    {
        title: t('columns.companyPhone'),
        name: CustomersMembersListDataGridCellKeys.companyPhone,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
        width: 150,
    },
    {
        title: t('columns.country'),
        name: CustomersMembersListDataGridCellKeys.country,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
    },
    {
        title: t('columns.province'),
        name: CustomersMembersListDataGridCellKeys.province,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
    },
    {
        title: t('columns.city'),
        name: CustomersMembersListDataGridCellKeys.city,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
    },
    {
        title: t('columns.address'),
        name: CustomersMembersListDataGridCellKeys.address,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
        width: 200,
    },
    {
        title: t('columns.postalCode'),
        name: CustomersMembersListDataGridCellKeys.postalCode,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.string,
    },
    {
        title: t('columns.registerDate'),
        name: CustomersMembersListDataGridCellKeys.registerDate,
        alignment: DataGridColumnAlignments.left,
        type: DataGridColumnTypes.dateTime,
    },
    ...(showActions ? [buildActionsColumn(t, actionsWidth)] : []),
]);
